import { Random } from '@educraft/base';

/**
 * Groups generated items into buckets by a key function.
 * Within each bucket, items are deduplicated by the display (texts) function.
 *
 * getRandom() picks one bucket at random as the "good" pool and treats
 * all other buckets as the "bad" pool — returning the stored texts arrays
 * for both, ready to join and display.
 *
 * Equivalent to C# PoolDict<T> used in Bludiste and similar generators.
 *
 * Example:
 *   const pool = new PoolDict(q => q.answer, q => q.texts);
 *   pool.add({ texts: ['1 + 3 ='], answer: '4' });
 *   pool.add({ texts: ['2 + 2 ='], answer: '4' });  // same bucket as above
 *   pool.add({ texts: ['1 + 1 ='], answer: '2' });  // different bucket
 *   pool.add({ texts: ['2 + 2 ='], answer: '4' });  // duplicate → deduplicated
 *   // Buckets: { '2': [['1 + 1 =']], '4': [['1 + 3 ='], ['2 + 2 =']] }
 */
export class PoolDict<T> {
  // bucketKey → Map<displayKey (joined texts), texts[]>
  private readonly buckets = new Map<string, Map<string, T>>();
  isInteger = true;

  constructor(
    private readonly bucketFn: (item: T) => string,
    private readonly displayFn: (item: T) => string
  ) {}

  add(item: T): void {
    const bucketKey = this.bucketFn(item);
    const displayKey = this.displayFn(item);

    if (!this.buckets.has(bucketKey)) {
      this.buckets.set(bucketKey, new Map<string, T>());
      const parsed = Number(bucketKey);
      this.isInteger = this.isInteger && Number.isInteger(parsed);
    }
    // Deduplicate within bucket by display key
    this.buckets.get(bucketKey)!.set(displayKey, item);
  }

  /**
   * Pick one bucket from top 1/3 buckets (by item count) as "good".
   * Then pick from other buckets based on isInteger flag.
   * Returns arrays of texts[] for each pool.
   */
  getRandom(rnd: Random): { good: T[]; bad: T[] } {
    const bucketEntries = [...this.buckets.entries()];
    if (bucketEntries.length === 0) return { good: [], bad: [] };

    const sorted = bucketEntries.sort((a, b) => b[1].size - a[1].size);
    const topThirdCount = Math.max(1, Math.ceil(sorted.length / 3));
    const topBuckets = sorted.slice(0, topThirdCount);

    const goodEntry = topBuckets[rnd.next(0, topBuckets.length)];
    const goodKey = goodEntry[0];
    const good = [...goodEntry[1].values()];

    const goodCount = good.length;
    const bad: T[] = [];

    const otherBuckets = sorted.filter(([key]) => key !== goodKey);

    if (this.isInteger) {
      const goodNum = Number(goodKey);
      const sortedOther = otherBuckets.sort(
        (a, b) => Math.abs(Number(a[0]) - goodNum) - Math.abs(Number(b[0]) - goodNum)
      );

      let taken = 0;
      for (const [, bucket] of sortedOther) {
        const items = [...bucket.values()];
        for (const item of items) {
          if (taken >= goodCount) break;
          bad.push(item);
          taken++;
        }
        if (taken >= goodCount) break;
      }
    } else {
      const shuffled = otherBuckets.sort(() => rnd.next(0, 2) - 0.5);

      let taken = 0;
      for (const [, bucket] of shuffled) {
        const items = [...bucket.values()];
        for (const item of items) {
          if (taken >= goodCount) break;
          bad.push(item);
          taken++;
        }
        if (taken >= goodCount) break;
      }
    }

    return { good, bad };
  }

  /**
   * Get all bucket keys that have at least minItems items.
   */
  getBucketKeys(minItems: number): string[] {
    const result: string[] = [];
    for (const [key, bucket] of this.buckets.entries()) {
      if (bucket.size >= minItems) {
        result.push(key);
      }
    }
    return result;
  }

  /**
   * Get all items in a bucket by answer key.
   */
  getBucket(key: string): T[] {
    const bucket = this.buckets.get(key);
    return bucket ? [...bucket.values()] : [];
  }
}
